import { FloatingLoader, Skeleton } from "@/components";
import { BackAppBar } from "@/components/ui";
import useReminderMutation from "@/hooks/useReminderMutation";
import { QUERY_KEYS } from "@/libraries/constant";
import { getReminders } from "@/queries";
import { onlineManager, useInfiniteQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import compact from "lodash/compact";
import { useEffect, useMemo, useRef } from "react";
import { useInView } from "react-intersection-observer";
import EmptyInbox from "../-components/EmptyInbox";
import ReminderCard from "../-components/ReminderCard";

export const Route = createFileRoute("/dashboard/inbox/reminders/completed")({
	loader: ({ context }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return context.queryClient.ensureInfiniteQueryData(getReminders());
		}
		return context.queryClient.getQueryData([QUERY_KEYS.GET_REMINDERS]);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const [loadMoreRef, isLoadMoreRef] = useInView();
	const scrollRef = useRef<HTMLDivElement>(null);

	const remindersQuery = useInfiniteQuery(getReminders());
	const reminders = useMemo(() => {
		const items = compact(remindersQuery.data || []);
		return items.filter((i) => i.is_done);
	}, [remindersQuery.data]);

	const { checked } = useReminderMutation();

	const isLoading = remindersQuery.isLoading || remindersQuery.isFetching;
	const isEmpty = useMemo(
		() => !isLoading && !reminders.length,
		[isLoading, reminders],
	);
	const isEmptySearch = useMemo(
		() => !isLoading && !reminders.length,
		[isLoading, reminders],
	);

	const reminderVirtualizer = useVirtualizer({
		count: reminders.length,
		overscan: 5,
		gap: 12,
		getScrollElement: () => scrollRef.current,
		measureElement: (el) => el.getBoundingClientRect().height,
		estimateSize: () => 50,
	});
	const reminderVirtualizerItems = reminderVirtualizer.getVirtualItems();

	const onClickRadio = (
		e: React.MouseEvent<HTMLButtonElement>,
		reminderId: number,
		is_done: boolean,
	) => {
		e.stopPropagation();
		e.preventDefault();
		return checked.mutate({
			id: reminderId,
			is_done: !is_done,
		});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (isLoadMoreRef && !remindersQuery.isFetchingNextPage) {
			remindersQuery.fetchNextPage();
		}
	}, [isLoadMoreRef, remindersQuery.isFetchingNextPage]);

	return (
		<>
			<BackAppBar
				className="!bg-gradient-contact [&_*_]:text-white"
				title="Complete Reminders"
			/>
			<div
				ref={scrollRef}
				className="relative h-full w-full overflow-y-auto py-4"
			>
				<FloatingLoader open={isLoading || remindersQuery.isFetchingNextPage} />
				{!isEmpty || !isEmptySearch ? (
					<div
						className="relative w-full"
						style={{
							height: reminderVirtualizer.getTotalSize(),
						}}
					>
						{reminderVirtualizerItems.map((reminderVirtualizerItem) => {
							const reminder = reminders[reminderVirtualizerItem.index];
							return (
								<Link
									key={reminderVirtualizerItem.key}
									data-index={reminderVirtualizerItem.index}
									ref={reminderVirtualizer.measureElement}
									className="container w-full absolute top-0 left-0"
									style={{
										transform: `translateY(${reminderVirtualizerItem.start}px)`,
									}}
									to="/dashboard/inbox/reminders/$id"
									params={{ id: reminder.id }}
								>
									<ReminderCard
										reminder={reminder}
										onClickRadio={onClickRadio}
									/>
								</Link>
							);
						})}
						{remindersQuery.hasNextPage &&
						!remindersQuery.isFetchingNextPage ? (
							<div className="container">
								<Skeleton ref={loadMoreRef} className="w-full h-28" />
							</div>
						) : null}
					</div>
				) : null}
				{isEmpty ? <EmptyInbox title="No completed reminders" /> : null}
			</div>
		</>
	);
}
